import clsx from "clsx";
import format from "date-fns/format";
import formatISO from "date-fns/formatISO";
import isFirstDayOfMonth from "date-fns/isFirstDayOfMonth";
import isLastDayOfMonth from "date-fns/isLastDayOfMonth";
import isSameDay from "date-fns/isSameDay";
import isToday from "date-fns/isToday";
import parseISO from "date-fns/parseISO";
import React, { useEffect, useState } from "react";

import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import { useTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Comment from "@material-ui/icons/Comment";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { EMPTY_STRING } from "common/constants";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import ScheduleDateMenu from "components/ScheduleDateMenu";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { useMutationWithRefetch } from "hooks/useMutationWithRefetch";
import ICalendarDay from "interfaces/ICalendarDay";
import { shouldShutOffDate } from "utils/date-utils";
import { WORKOUT_REQUEST_GET_OPTIONS } from "../options";

import useStyles from "./styles";

enum EDataTestState {
  AVAILABLE = "AVAILABLE",
  UNAVAILABLE = "UNAVAILABLE",
  SCHEDULED = "SCHEDULED"
}

export interface Props {
  dayDate: string;
  workoutRequest?: racehorse360.IWorkoutRequest;
  horse: racehorse360.IHorse;
  facilityBlackoutsCalendar?: ICalendarDay[];
  className?: string;
  onUpdate?: () => Promise<void>;
}

const ScheduleDay = (props: Props) => {
  const {
    dayDate,
    workoutRequest,
    horse,
    facilityBlackoutsCalendar = [],
    className,
    onUpdate
  } = props;

  const { currentUser } = useLoggedInUser();

  const theme = useTheme();
  const matchesUpSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [isDateMenuOpen, setIsDateMenuOpen] = useState(false);
  const [dateMenuAnchor, setDateMenuAnchor] = useState<HTMLDivElement>(null);
  const [activeWorkoutRequest, setActiveWorkoutRequest] =
    useState<racehorse360.IWorkoutRequest>(workoutRequest);

  const classes = useStyles({
    backgroundColor: activeWorkoutRequest?.facility?.backgroundColor,
    strokeColor: activeWorkoutRequest?.facility?.strokeColor
  });

  const selectedFacility = useAppSelector(
    state => state.workoutsPage.selectedFacility
  );

  const facility = activeWorkoutRequest?.facility;
  const facilityCode = facility?.code.trim() || EMPTY_STRING;

  const hasWorkoutRequest = Boolean(activeWorkoutRequest);
  const date = parseISO(dayDate);

  const isFacilityBlackout = facilityBlackoutsCalendar.some(
    day => isSameDay(day.date, date) && day.facilityBlackout
  );
  console.log(`isFacilityBlackout: ${isFacilityBlackout}`);
  const isShutOffDate =
    selectedFacility && shouldShutOffDate(date, selectedFacility);
  console.log(`isShutOffDate: ${isShutOffDate}`);
  const shouldDisableDate =
    !hasWorkoutRequest && [isFacilityBlackout, isShutOffDate].some(Boolean);
  console.log(`shouldDisableDate: ${shouldDisableDate}`);

  const shouldShowScheduleDateMenu = [
    isDateMenuOpen,
    activeWorkoutRequest
  ].every(Boolean);

  const shouldShowMonthDivider =
    isFirstDayOfMonth(date) || isLastDayOfMonth(date);

  let dataTestState = EDataTestState.AVAILABLE;
  if (hasWorkoutRequest) {
    dataTestState = EDataTestState.SCHEDULED;
  } else if (!hasWorkoutRequest && shouldDisableDate) {
    dataTestState = EDataTestState.UNAVAILABLE;
  }

  useEffect(() => {
    setActiveWorkoutRequest(workoutRequest);
  }, [workoutRequest]);

  const {
    useCreateWorkoutRequest,
    useCancelWorkoutRequest,
    useMoveFacilityWorkoutRequest
  } = useRacehorse360Api();

  const { mutateAsync: createWorkoutRequest } = useCreateWorkoutRequest();
  const { mutateAsync: cancelWorkoutRequest } = useCancelWorkoutRequest();
  const { mutateAsync: moveFacilityWorkoutRequest } =
    useMoveFacilityWorkoutRequest();

  const moveFacilityWorkoutRequestTransaction = useMutationWithRefetch({
    mutation: moveFacilityWorkoutRequest,
    refetch: onUpdate
  });

  const cancelWorkoutRequestTransaction = useMutationWithRefetch({
    mutation: cancelWorkoutRequest,
    refetch: onUpdate
  });

  const createWorkoutRequestTransaction = useMutationWithRefetch({
    mutation: createWorkoutRequest,
    refetch: onUpdate
  });

  const isLoading = [
    moveFacilityWorkoutRequestTransaction.isLoading,
    cancelWorkoutRequestTransaction.isLoading,
    createWorkoutRequestTransaction.isLoading
  ].some(Boolean);

  const handleDateMenuClose = () => {
    setIsDateMenuOpen(false);
  };

  const handleCardClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    if (createWorkoutRequestTransaction.isLoading) return;

    if (hasWorkoutRequest) {
      setDateMenuAnchor(event.currentTarget);
      setIsDateMenuOpen(true);
    } else {
      const createdWorkoutRequest = await createWorkoutRequestTransaction.run({
        date: dayDate,
        facilityId: selectedFacility.id,
        horseId: horse.id,
        getOptions: {
          select: WORKOUT_REQUEST_GET_OPTIONS
        }
      });

      setActiveWorkoutRequest(createdWorkoutRequest);
    }
  };

  const handleChangeWorkoutRequest = async (
    workoutData: racehorse360.IWorkoutRequest
  ) => {
    if (
      cancelWorkoutRequestTransaction.isLoading ||
      moveFacilityWorkoutRequestTransaction.isLoading
    )
      return;

    if (workoutData.facility) {
      const updatedWorkoutRequest =
        await moveFacilityWorkoutRequestTransaction.run({
          id: workoutData.id,
          facilityId: workoutData.facility.id,
          getOptions: {
            select: WORKOUT_REQUEST_GET_OPTIONS
          }
        });

      setActiveWorkoutRequest(updatedWorkoutRequest);
    } else {
      await cancelWorkoutRequestTransaction.run({ id: workoutData.id });
      setActiveWorkoutRequest(null);
    }
  };

  const renderCardHeader = () => {
    return (
      <>
        {hasWorkoutRequest && (
          <CardHeader
            className={classes.cardHeader}
            disableTypography
            title={facilityCode}
          />
        )}

        <div
          className={clsx(
            classes.cardHeaderFiller,
            classes.cardDetailedDefaultHeader
          )}
        />
      </>
    );
  };

  const renderCardActions = () => {
    if (hasWorkoutRequest) {
      return (
        <CardActions className={classes.cardActions}>
          <MoreHorizIcon className={classes.moreHorizIcon} />

          {renderCommentIcon()}
        </CardActions>
      );
    } else {
      return (
        <div
          className={clsx(
            classes.cardActions,
            classes.cardDetailedDefaultActions
          )}
        />
      );
    }
  };

  const renderCommentIcon = () => {
    const hasComment = currentUser.isRacingOfficial
      ? activeWorkoutRequest.hasRacingSecretaryComment
      : activeWorkoutRequest.hasComment;

    if (hasComment) {
      return <Comment className={classes.commentIcon} />;
    }
  };

  return (
    <Card
      className={clsx(classes.card, className, classes.cardDetailed, {
        [classes.cardDisabled]: shouldDisableDate,
        [classes.default]: !hasWorkoutRequest
      })}
      variant={"outlined"}
      onClick={handleCardClick}
      data-test-element="CALENDAR_DAY"
      data-test-value={formatISO(date, { representation: "date" })}
      data-test-state={dataTestState}
    >
      {isLoading && <Loader className={classes.loader} />}

      <Typography className={classes.dayName}>
        {format(date, "E").toUpperCase()}
      </Typography>

      {renderCardHeader()}

      <CardContent className={classes.cardContent}>
        <Box className={classes.dateWrapper}>
          {shouldShowMonthDivider && (
            <Typography className={clsx(classes.dateMonth)}>
              {format(date, matchesUpSm ? "MMMM" : "MMM")}
            </Typography>
          )}
          <Typography
            className={clsx(classes.date, {
              [classes.squared]: isToday(date)
            })}
          >
            {date.getDate()}
          </Typography>
        </Box>
      </CardContent>

      {renderCardActions()}

      {shouldShowScheduleDateMenu && (
        <ScheduleDateMenu
          horse={horse}
          anchorEl={dateMenuAnchor}
          workoutData={workoutRequest}
          onChange={handleChangeWorkoutRequest}
          onClose={handleDateMenuClose}
          isOpen={isDateMenuOpen}
        />
      )}
    </Card>
  );
};

export default React.memo(ScheduleDay);
